(() => {
  window.QePrescriptionPriceHandled = true;

  const getGuideSection = (heading, isFirstHeading) => {
    const normalized = heading.toLowerCase().replace(/\s+/g, ' ').trim();

    if (normalized === 'overview' || isFirstHeading) {
      return { key: 'overview', keepHeading: false };
    }
    if (
      normalized.includes('key benefit')
      || normalized.includes('key feature')
      || normalized === 'benefits'
      || normalized === 'features'
    ) {
      return { key: 'benefits', keepHeading: false };
    }
    if (normalized.startsWith('who ') || normalized.includes('best for')) {
      return { key: 'who', keepHeading: false };
    }
    if (
      normalized === 'what makes it special'
      || normalized.startsWith('what makes')
      || normalized.includes('specification')
      || normalized === 'product details'
    ) {
      return { key: 'special', keepHeading: false };
    }
    if (normalized === 'how it works') {
      return { key: 'how-it-works', keepHeading: false };
    }
    if (/^how .*compare/.test(normalized) || normalized.includes('comparison')) {
      return { key: 'how-it-works', keepHeading: true };
    }
    if (normalized === 'how to use' || normalized === 'how to order') {
      return { key: 'use', keepHeading: false };
    }

    return null;
  };

  const populateGuideFromDescription = (guide) => {
    const source = guide.querySelector('[data-qe-guide-description]');
    if (!source) return;

    let sourceContainer = source;
    while (
      sourceContainer.children.length === 1
      && /^(DIV|SECTION|ARTICLE)$/.test(sourceContainer.firstElementChild.tagName)
      && sourceContainer.firstElementChild.querySelector('h2, h3, h4')
    ) {
      sourceContainer = sourceContainer.firstElementChild;
    }

    const sourceNodes = [...sourceContainer.children];
    const headingNodes = sourceNodes.filter((node) => /^H[2-4]$/.test(node.tagName));
    if (!headingNodes.length) return;
    const fragments = {
      overview: document.createDocumentFragment(),
      benefits: document.createDocumentFragment(),
      who: document.createDocumentFragment(),
      special: document.createDocumentFragment(),
      'how-it-works': document.createDocumentFragment(),
      use: document.createDocumentFragment()
    };
    let currentKey = 'overview';
    let topLevelHeadingIndex = 0;
    let mappedSectionCount = 0;

    sourceNodes.forEach((node) => {
      const isSectionHeading = /^H[2-4]$/.test(node.tagName);

      if (isSectionHeading) {
        const section = getGuideSection(node.textContent || '', topLevelHeadingIndex === 0);
        topLevelHeadingIndex += 1;

        if (section) {
          currentKey = section.key;
          if (section.key !== 'overview') mappedSectionCount += 1;

          if (section.keepHeading) {
            const subheading = document.createElement('h3');
            subheading.innerHTML = node.innerHTML;
            fragments[currentKey].appendChild(subheading);
          }
          return;
        }

        const subheading = document.createElement('h3');
        subheading.innerHTML = node.innerHTML;
        fragments[currentKey].appendChild(subheading);
        return;
      }

      if (node.tagName === 'HR') return;
      fragments[currentKey].appendChild(node.cloneNode(true));
    });

    if (!mappedSectionCount) return;

    ['overview', 'benefits', 'who', 'special', 'how-it-works', 'use'].forEach((key) => {
      const panel = guide.querySelector(`[data-qe-guide-panel="${key}"]`);
      const tab = guide.querySelector(`[data-qe-guide-tab="${key}"]`);
      const fragment = fragments[key];
      const hasDescriptionContent = fragment && fragment.childNodes.length > 0;

      if (hasDescriptionContent && panel) {
        panel.replaceChildren(fragment);
      } else if (key !== 'overview' && key !== 'use' && tab) {
        tab.hidden = true;
      }
    });
  };

  const initPrescriptionPdp = (root) => {
    if (root.dataset.qeRxReady === 'true') return;
    root.dataset.qeRxReady = 'true';
    const packField = root.querySelector('.qe-rx-pack-field');

    // The prescription app renders custom DIV controls and can replace either eye.
    // Keep its handlers and fields, but expose names and keyboard activation.
    const enhancePrescriptionControls = () => {
      const form = root.querySelector('.sb-eye-lens-form-wrap');
      if (!form) return;
      const eyeFor = (element) => element.closest('.left_prescription_list, .input-number-left, .left_lence')
        || element.getAttribute('apply_for') === 'left' ? 'Left' : 'Right';
      const asButton = (element, name, disabled = false) => {
        element.setAttribute('role', 'button');
        element.setAttribute('tabindex', disabled ? '-1' : '0');
        element.setAttribute('aria-disabled', String(disabled));
        if (name) element.setAttribute('aria-label', name);
        element.dataset.qeRxKeyboard = '';
      };
      form.querySelectorAll('select[apply_for]').forEach((select) => {
        const label = select.getAttribute('input_name') || select.parentElement.querySelector('label')?.textContent.trim();
        if (label) select.setAttribute('aria-label', `${eyeFor(select)} ${label}`);
      });
      form.querySelectorAll('.input-number').forEach((input) => {
        input.setAttribute('aria-label', `${eyeFor(input)} boxes per eye`);
        input.setAttribute('inputmode', 'numeric');
      });
      form.querySelectorAll('.input-number-increment, .input-number-decrement').forEach((button) => {
        const eye = button.closest('.left_lence') ? 'Left' : 'Right';
        const enabled = form.querySelector(`.${eye.toLowerCase()}_eye_lens`)?.checked;
        asButton(button, `${button.classList.contains('input-number-increment') ? 'Increase' : 'Decrease'} ${eye.toLowerCase()} boxes`, enabled === false);
      });
      form.querySelectorAll('.select-power-sphere').forEach((button) => {
        const eye = eyeFor(button);
        const panel = button.parentElement.querySelector('.select-power-drp');
        const enabled = form.querySelector(`.${eye.toLowerCase()}_eye_lens`)?.checked;
        const value = button.querySelector('span')?.textContent.trim();
        asButton(button, `${eye} Power/Sphere${value ? `, ${value}` : ''}`, enabled === false);
        button.setAttribute('aria-expanded', String(panel?.classList.contains('options-visible') || false));
        if (panel) {
          panel.id = `${root.id}-power-${eye.toLowerCase()}`;
          panel.setAttribute('role', 'radiogroup');
          panel.setAttribute('aria-label', `${eye} Power/Sphere`);
          button.setAttribute('aria-controls', panel.id);
        }
      });
      form.querySelectorAll('.product_btn.continue_button_label:not(.remove-btn)').forEach((button) => {
        asButton(button, '', !button.classList.contains('final_form_buton'));
      });
      form.querySelectorAll('.page_level_error, .lens_err').forEach((error) => error.setAttribute('role', 'alert'));
    };
    root.addEventListener('keydown', (event) => {
      const button = event.target.closest('[data-qe-rx-keyboard]');
      if (button && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        if (button.getAttribute('aria-disabled') !== 'true') button.click();
      }
      if (event.key === 'Escape') {
        const parent = event.target.closest('.select-power-sphere-parent');
        const panel = parent?.querySelector('.select-power-drp.options-visible');
        if (panel) {
          event.preventDefault();
          parent.querySelector('.select-power-sphere').click();
          parent.querySelector('.select-power-sphere').focus();
        }
      }
    });
    root.addEventListener('change', (event) => {
      if (event.target.matches('input[name="select-power-right"], input[name="select-power-left"]')
        && event.target === document.activeElement) {
        window.setTimeout(() => event.target.closest('.select-power-sphere-parent')?.querySelector('.select-power-sphere')?.focus(), 0);
      }
    });
    let accessibilityFrame = null;
    const scheduleAccessibility = () => {
      if (accessibilityFrame !== null) return;
      accessibilityFrame = window.requestAnimationFrame(() => {
        accessibilityFrame = null;
        enhancePrescriptionControls();
      });
    };
    new MutationObserver(scheduleAccessibility).observe(root, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'disabled']
    });
    root.addEventListener('change', scheduleAccessibility);
    enhancePrescriptionControls();

    const positionPrescriptionForm = () => {
      const purchaseForm = root.querySelector('.qe-rx-app-mount');
      const prescriptionForm = root.querySelector('.sb-eye-lens-form-wrap');
      if (!purchaseForm || !prescriptionForm) return;

      if (prescriptionForm.nextElementSibling !== purchaseForm) {
        purchaseForm.before(prescriptionForm);
      }

      const duplicateHeading = prescriptionForm.querySelector(
        '.manual_description_form .prescription_wrapper_title'
      );
      if (duplicateHeading) duplicateHeading.hidden = true;
      const parameters = prescriptionForm.querySelector('.parameters-form');
      if (packField && parameters && packField.nextElementSibling !== parameters) {
        parameters.before(packField);
      }
    };

    const positionPurchaseOptions = () => {
      const purchaseOptions = root.querySelector('.qe-rx-app-mount > .sealsubs-target-element')
        || root.querySelector('.qe-rx-purchase-options');
      const prescriptionForm = root.querySelector('.sb-eye-lens-form-wrap');
      const finalButton = prescriptionForm
        ? [...prescriptionForm.querySelectorAll('.final_form_buton')]
          .find((button) => !button.classList.contains('remove-btn'))
        : null;
      const buttonRow = finalButton ? finalButton.closest('.no-gutter') : null;

      if (!purchaseOptions || !buttonRow) return;
      purchaseOptions.classList.add('qe-rx-purchase-options');
      if (purchaseOptions.nextElementSibling !== buttonRow) {
        buttonRow.before(purchaseOptions);
      }
    };

    const positionPurchaseControls = () => {
      positionPrescriptionForm();
      positionPurchaseOptions();
    };

    const priceDataNode = root.querySelector('[data-qe-rx-price-data]');
    let priceData = { currency: 'GBP', oneTime: 0, plans: {} };
    if (priceDataNode) {
      try {
        priceData = Object.assign(priceData, JSON.parse(priceDataNode.textContent));
      } catch (error) {
        console.warn('Quality Eyes price data could not be read.', error);
      }
    }

    const formatMoney = (cents) => new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: priceData.currency || 'GBP',
      minimumFractionDigits: 2
    }).format((Number(cents) || 0) / 100);

    const selectedUnitPrice = () => {
      const selectedOption = root.querySelector('.sls-option:checked')
        || root.querySelector('.sls-option-container.sls-active .sls-option');
      if (selectedOption?.dataset.type !== 'subscription') return Number(priceData.oneTime) || 0;

      const interval = selectedOption.closest('.sls-option-container')?.querySelector('select[name="subs_interval"]');
      if (interval && priceData.plans[interval.value] !== undefined) {
        return Number(priceData.plans[interval.value]) || 0;
      }

      return Number(priceData.oneTime) || 0;
    };

    const selectedQuantity = () => {
      const prescriptionForm = root.querySelector('.sb-eye-lens-form-wrap');
      if (!prescriptionForm) return 0;

      let total = 0;
      prescriptionForm.querySelectorAll('.lens-quantity-selector-form input').forEach((input) => {
        const quantitySelector = input.closest('.lens-quantity-selector-form');
        if (!quantitySelector || window.getComputedStyle(quantitySelector).display === 'none') return;

        const quantity = Number.parseInt(input.value, 10);
        if (Number.isFinite(quantity) && quantity > 0) total += quantity;
      });
      return total;
    };

    const ensureTotalPrice = () => {
      const prescriptionForm = root.querySelector('.sb-eye-lens-form-wrap');
      if (!prescriptionForm) return null;

      let container = prescriptionForm.querySelector('#elp-total-price-container');
      if (!container) {
        const finalButton = [...prescriptionForm.querySelectorAll('.final_form_buton')]
          .find((button) => !button.classList.contains('remove-btn'));
        if (!finalButton) return null;

        container = document.createElement('div');
        container.id = 'elp-total-price-container';
        container.innerHTML = '<div class="elp-total-price-wrapper"><div class="elp-total-label">Total Price:</div><div class="elp-total-amount" id="elp-total-amount"></div></div>';
        finalButton.parentNode.insertBefore(container, finalButton);
      }
      return container.querySelector('#elp-total-amount');
    };

    let priceUpdateFrame = null;
    const updateDisplayedPrices = () => {
      priceUpdateFrame = null;
      const unitPrice = selectedUnitPrice();
      const formattedUnitPrice = formatMoney(unitPrice);
      const topPrice = root.querySelector('.qe-pdp-buy__pricebar .price-item--regular');
      if (topPrice && topPrice.textContent.trim() !== formattedUnitPrice) {
        topPrice.textContent = formattedUnitPrice;
      }
      const comparePrice = root.querySelector('[data-qe-compare-price]');
      if (comparePrice) {
        comparePrice.hidden = !(Number(priceData.compareAt) > Number(priceData.oneTime)
          && Number(priceData.compareAt) > unitPrice);
        const formattedComparePrice = formatMoney(priceData.compareAt);
        if (comparePrice.textContent !== formattedComparePrice) comparePrice.textContent = formattedComparePrice;
      }

      const totalAmount = ensureTotalPrice();
      const formattedTotal = formatMoney(unitPrice * selectedQuantity());
      if (totalAmount && totalAmount.textContent.trim() !== formattedTotal) {
        totalAmount.textContent = formattedTotal;
      }
    };

    const schedulePriceUpdate = () => {
      if (priceUpdateFrame !== null) return;
      priceUpdateFrame = window.requestAnimationFrame(updateDisplayedPrices);
    };

    positionPurchaseControls();
    const prescriptionObserver = new MutationObserver(positionPurchaseControls);
    prescriptionObserver.observe(root, { childList: true, subtree: true });
    window.setTimeout(() => prescriptionObserver.disconnect(), 10000);

    root.addEventListener('change', schedulePriceUpdate);
    root.addEventListener('input', schedulePriceUpdate);
    root.addEventListener('click', () => {
      [50, 250, 750].forEach((delay) => window.setTimeout(schedulePriceUpdate, delay));
    });
    const priceObserver = new MutationObserver(schedulePriceUpdate);
    priceObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'checked', 'value'] });
    window.setTimeout(() => priceObserver.disconnect(), 15000);
    schedulePriceUpdate();

    const galleryMain = root.querySelector('[data-qe-gallery-main]');
    const galleryThumbs = [...root.querySelectorAll('[data-qe-gallery-thumb]')];

    galleryThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        galleryThumbs.forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });

        if (!galleryMain || !thumb.dataset.image) return;
        galleryMain.style.opacity = '0';
        window.setTimeout(() => {
          galleryMain.src = thumb.dataset.image;
          galleryMain.alt = thumb.dataset.alt || '';
          galleryMain.classList.toggle('is-photo', thumb.classList.contains('qe-pdp-gallery__thumb--photo'));
          galleryMain.style.opacity = '1';
        }, 120);
      });
    });

    const variantSelect = root.querySelector('[data-qe-rx-variant]');
    if (variantSelect) {
      variantSelect.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantSelect.value);
        window.location.assign(url.toString());
      });
    }

    const guide = root.querySelector('[data-qe-guide]');
    if (guide) {
      populateGuideFromDescription(guide);

      const tabs = [...guide.querySelectorAll('[data-qe-guide-tab]')];
      const panels = [...guide.querySelectorAll('[data-qe-guide-panel]')];

      tabs.forEach((tab) => {
        const key = tab.dataset.qeGuideTab;
        const panel = guide.querySelector(`[data-qe-guide-panel="${key}"]`);
        if (key !== 'overview' && (!panel || !panel.textContent.trim())) {
          tab.hidden = true;
        }
      });

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const target = tab.dataset.qeGuideTab;
          tabs.forEach((item) => {
            const active = item === tab;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-selected', String(active));
          });
          panels.forEach((panel) => {
            const active = panel.dataset.qeGuidePanel === target;
            panel.classList.toggle('is-active', active);
            panel.hidden = !active;
          });
        });
      });
    }
  };

  const initAll = () => document.querySelectorAll('[data-qe-rx-pdp]').forEach(initPrescriptionPdp);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
